<script lang="ts">
  /**
   * /learn — Notion-style two-column index.
   *
   *   ┌──────────┬──────────────────────────────┐
   *   │ Пәндер   │ Selected subject header      │
   *   │ sidebar  │ ─────────                    │
   *   │ 240px    │ Topic cards (stacked list)   │
   *   └──────────┴──────────────────────────────┘
   *
   * Selecting a subject flips --accent via [data-subject] on <html>,
   * matching the home page behaviour. All state is local — filtering
   * is pure client-side over the prefetched topic list.
   */
  import { onMount } from 'svelte';
  import { SUBJECTS, subjectLabel, type SubjectId } from '$components/shared/subjects';

  let { data } = $props();

  let active = $state<SubjectId>('history');

  function applySubjectAttr(s: string): void {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute('data-subject', s);
    try {
      localStorage.setItem('selectedSubject', s);
    } catch {
      /* ignore */
    }
  }

  $effect(() => {
    applySubjectAttr(active);
  });

  onMount(() => {
    try {
      const saved = localStorage.getItem('selectedSubject');
      if (saved && SUBJECTS.some((s) => s.id === saved)) {
        active = saved as SubjectId;
      }
    } catch {
      /* ignore */
    }
  });

  const activeTopics = $derived(data.topics.filter((t) => t.subject === active));
  const activeLabel = $derived(subjectLabel(active));
  const topicCounts = $derived(
    SUBJECTS.reduce<Record<string, number>>((acc, s) => {
      acc[s.id] = data.topics.filter((t) => t.subject === s.id).length;
      return acc;
    }, {})
  );

  function formatDate(iso: string): string {
    try {
      return new Date(iso).toLocaleDateString('kk-KZ', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return iso;
    }
  }
</script>

<svelte:head>
  <title>Оқу материалдары — Zetix</title>
  <meta
    name="description"
    content="Пәндер бойынша терең түсіндірмелер — ҰБТ-ға дайындыққа арналған оқу материалдары."
  />
  {@html `<script type="application/ld+json">${JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: data.topics.map((t, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `https://zetix.kz/learn/${t.id}`,
      name: t.title
    }))
  })}</` + `script>`}
</svelte:head>

<section class="learn">
  <header class="hero">
    <h1 class="hero-title">Оқу материалдары</h1>
    <p class="hero-sub">Пәндер бойынша терең түсіндірмелер</p>
  </header>

  <div class="layout">
    <!-- Sidebar -->
    <aside class="sidebar" aria-label="Пәндер">
      <p class="sidebar-label">Пәндер</p>
      <nav class="subject-nav">
        {#each SUBJECTS as s (s.id)}
          {@const count = topicCounts[s.id] ?? 0}
          <button
            type="button"
            class="subject-item"
            class:active={active === s.id}
            onclick={() => (active = s.id)}
            aria-pressed={active === s.id}
          >
            <span class="subject-dot" aria-hidden="true"></span>
            <span class="subject-text">{s.label}</span>
            {#if count > 0}
              <span class="subject-count">{count}</span>
            {/if}
          </button>
        {/each}
      </nav>
    </aside>

    <!-- Main pane -->
    <div class="main">
      <div class="subject-header">
        <h2 class="subject-title">{activeLabel}</h2>
        <p class="subject-sub">Тақырыптар · {activeTopics.length}</p>
      </div>

      {#if activeTopics.length === 0}
        <div class="empty">
          <p>Бұл пән бойынша әзірге материал жоқ. Жақын арада қосылады.</p>
        </div>
      {:else}
        <ul class="topic-list">
          {#each activeTopics as topic (topic.id)}
            <li>
              <a href={`/learn/${topic.id}`} class="topic-card">
                <span class="topic-icon" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                  </svg>
                </span>
                <span class="topic-body">
                  <span class="topic-title">{topic.title}</span>
                  <span class="topic-desc">{topic.description}</span>
                </span>
                <time class="topic-date" datetime={topic.updatedAt}>
                  {formatDate(topic.updatedAt)}
                </time>
              </a>
            </li>
          {/each}
        </ul>
      {/if}
    </div>
  </div>
</section>

<style>
  .learn {
    max-width: var(--container-max);
    margin: 0 auto;
    padding: var(--space-10) var(--space-6) var(--space-16);
  }

  .hero {
    margin-bottom: var(--space-8);
  }
  .hero-title {
    margin: 0 0 var(--space-2);
    font-size: var(--text-3xl);
    font-weight: var(--fw-semibold);
    line-height: var(--leading-tight);
    letter-spacing: -0.02em;
    color: var(--color-fg);
  }
  .hero-sub {
    margin: 0;
    color: var(--color-fg-muted);
    font-size: var(--text-base);
  }

  .layout {
    display: grid;
    grid-template-columns: 240px 1fr;
    gap: var(--space-10);
    align-items: start;
  }
  @media (max-width: 860px) {
    .layout {
      grid-template-columns: 1fr;
      gap: var(--space-6);
    }
  }

  /* --- Sidebar ----------------------------------------------------- */
  .sidebar {
    position: sticky;
    top: calc(var(--header-h) + var(--space-6));
    background: var(--color-bg-subtle);
    border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-md);
    padding: var(--space-3);
  }
  @media (max-width: 860px) {
    .sidebar { position: static; }
  }

  .sidebar-label {
    margin: 0 0 var(--space-2);
    padding: 0 var(--space-2);
    font-size: var(--text-xs);
    font-weight: var(--fw-medium);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--color-fg-muted);
  }

  .subject-nav {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .subject-item {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    width: 100%;
    padding: 6px var(--space-3);
    text-align: left;
    font-size: var(--text-sm);
    font-weight: var(--fw-normal);
    color: var(--color-fg-secondary);
    background: transparent;
    border: 0;
    border-radius: var(--radius-sm);
    cursor: pointer;
    transition: background-color var(--dur-fast) var(--ease),
                color var(--dur-fast) var(--ease);
  }
  .subject-item:hover {
    background: var(--color-bg-muted);
    color: var(--color-fg);
  }
  .subject-item.active {
    background: var(--accent-bg);
    color: var(--accent);
    font-weight: var(--fw-medium);
  }
  .subject-dot {
    width: 6px;
    height: 6px;
    border-radius: var(--radius-full);
    background: var(--color-fg-subtle);
    flex-shrink: 0;
  }
  .subject-item.active .subject-dot {
    background: var(--accent);
  }
  .subject-text {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .subject-count {
    font-size: var(--text-xs);
    color: var(--color-fg-muted);
    font-variant-numeric: tabular-nums;
  }
  .subject-item.active .subject-count {
    color: var(--accent);
    opacity: 0.75;
  }

  /* --- Main -------------------------------------------------------- */
  .subject-header {
    margin-bottom: var(--space-5);
    padding-bottom: var(--space-4);
    border-bottom: 1px solid var(--color-border-subtle);
  }
  .subject-title {
    margin: 0 0 var(--space-1);
    font-size: var(--text-2xl);
    font-weight: var(--fw-semibold);
    line-height: var(--leading-tight);
    color: var(--color-fg);
  }
  .subject-sub {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--color-fg-muted);
  }

  .topic-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .topic-card {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: var(--space-4);
    padding: var(--space-4);
    border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-md);
    background: var(--color-bg);
    color: inherit;
    transition: background-color var(--dur-fast) var(--ease),
                border-color var(--dur-fast) var(--ease);
  }
  .topic-card:hover {
    background: var(--color-bg-subtle);
    border-color: var(--color-border);
  }

  .topic-icon {
    display: inline-grid;
    place-items: center;
    width: 32px;
    height: 32px;
    border-radius: var(--radius-sm);
    background: var(--accent-bg);
    color: var(--accent);
    flex-shrink: 0;
  }

  .topic-body {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  .topic-title {
    font-size: var(--text-base);
    font-weight: var(--fw-medium);
    color: var(--color-fg);
    line-height: var(--leading-snug);
  }
  .topic-desc {
    font-size: var(--text-sm);
    color: var(--color-fg-muted);
    line-height: var(--leading-normal);
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .topic-date {
    font-size: var(--text-xs);
    color: var(--color-fg-muted);
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
  @media (max-width: 520px) {
    .topic-date { display: none; }
  }

  .empty {
    padding: var(--space-10);
    text-align: center;
    color: var(--color-fg-muted);
    font-size: var(--text-sm);
    border: 1px dashed var(--color-border);
    border-radius: var(--radius-md);
  }
</style>
